interface CountryProductSheetLocaleConfig {
  locales: string[];
  default: string;
}

export const getProductSheetLocaleByCountry = (
  country: string,
  locale: string,
  remoteCountriesAvailable?: readonly string[],
) => {
  const productSheetLocale = getProductSheetLocale(locale);
  const configLocalesByCountry: Record<
    string,
    CountryProductSheetLocaleConfig
  > = {
    AT: {
      locales: ['de-AT'],
      default: 'de-AT',
    },
    AU: {
      locales: ['en-AU'],
      default: 'en-AU',
    },
    BE: {
      locales: ['fr-BE', 'nl-BE'],
      default: 'fr-BE',
    },
    BR: {
      locales: ['pt-BR'],
      default: 'pt-BR',
    },
    CA: {
      locales: ['fr-CA', 'en-CA'],
      default: 'fr-CA',
    },
    CH: {
      locales: ['fr-CH', 'en-CH', 'de-CH', 'it-CH'],
      default: 'en-CH',
    },
    CL: {
      locales: ['es-CL'],
      default: 'es-CL',
    },
    CO: {
      locales: ['es-CO'],
      default: 'es-CO',
    },
    CZ: {
      locales: ['cs-CZ'],
      default: 'cs-CZ',
    },
    DE: {
      locales: ['de-DE'],
      default: 'de-DE',
    },
    DZ: {
      locales: ['fr-DZ'],
      default: 'fr-DZ',
    },
    EE: {
      locales: ['et-EE'],
      default: 'et-EE',
    },
    EG: {
      locales: ['ar-EG', 'en-EG'],
      default: 'en-EG',
    },
    ES: {
      locales: ['es-ES', 'ca-ES'],
      default: 'es-ES',
    },
    FR: {
      locales: ['fr-FR'],
      default: 'fr-FR',
    },
    GB: {
      locales: ['en-GB'],
      default: 'en-GB',
    },
    GF: {
      locales: ['fr-GF'],
      default: 'fr-GF',
    },
    GP: {
      locales: ['fr-GP'],
      default: 'fr-GP',
    },
    HR: {
      locales: ['hr-HR'],
      default: 'hr-HR',
    },
    HU: {
      locales: ['hu-HU'],
      default: 'hu-HU',
    },
    HK: {
      locales: ['zh-HK', 'en-HK'],
      default: 'zh-HK',
    },
    IE: {
      locales: ['en-IE'],
      default: 'en-IE',
    },
    IT: {
      locales: ['it-IT'],
      default: 'it-IT',
    },
    LT: {
      locales: ['lt-LT'],
      default: 'lt-LT',
    },
    LU: {
      locales: ['fr-LU', 'en-LU', 'de-LU'],
      default: 'fr-LU',
    },
    LV: {
      locales: ['lv-LV'],
      default: 'lv-LV',
    },
    MX: {
      locales: ['es-MX'],
      default: 'es-MX',
    },
    MQ: {
      locales: ['fr-MQ'],
      default: 'fr-MQ',
    },
    NL: {
      locales: ['nl-NL'],
      default: 'nl-NL',
    },
    PL: {
      locales: ['pl-PL'],
      default: 'pl-PL',
    },
    PT: {
      locales: ['pt-PT'],
      default: 'pt-PT',
    },
    RE: {
      locales: ['fr-RE'],
      default: 'fr-RE',
    },
    RO: {
      locales: ['ro-RO'],
      default: 'ro-RO',
    },
    RS: {
      locales: ['sr-RS'],
      default: 'sr-RS',
    },
    SG: {
      locales: ['en-SG'],
      default: 'en-SG',
    },
    SI: {
      locales: ['sl-SI'],
      default: 'sl-SI',
    },
    SK: {
      locales: ['sk-SK'],
      default: 'sk-SK',
    },
    TR: {
      locales: ['tr-TR'],
      default: 'tr-TR',
    },
    TW: {
      locales: ['zh-TW', 'en-TW'],
      default: 'zh-TW',
    },
  };

  const configByCountry: CountryProductSheetLocaleConfig | undefined =
    configLocalesByCountry?.[country];

  // No country config found, just return the "product sheet" locale
  if (!configByCountry) {
    return productSheetLocale;
  }

  // Product sheet locale is include in locales available for the country, return "product sheet" locale
  if (configByCountry?.locales?.includes(productSheetLocale)) {
    return productSheetLocale;
  }

  // Specific to visio store
  if (remoteCountriesAvailable && remoteCountriesAvailable.length > 0) {
    const configByRemoteCountry = new Set<string>();

    remoteCountriesAvailable?.forEach((remoteCountry) => {
      const locales = configLocalesByCountry?.[remoteCountry]?.locales;
      if (locales) {
        locales.forEach((locale) => configByRemoteCountry.add(locale));
      }
    });

    // Prefer the exact locale over another one sharing its language (e.g. nl-BE over nl-NL)
    if (configByRemoteCountry.has(productSheetLocale)) {
      return productSheetLocale;
    }

    const remoteLocale = Array.from(configByRemoteCountry)?.find(
      (localeString: string) => {
        return (
          new Intl.Locale(localeString)?.language ===
          new Intl.Locale(productSheetLocale)?.language
        );
      },
    );

    if (remoteLocale) {
      return remoteLocale;
    }
  }

  // Try to retrieve the most relevant locale using the language part of it among all locales available for the country.
  const potentialLocale = configByCountry?.locales?.find(
    (localeString: string) => {
      return (
        new Intl.Locale(localeString)?.language ===
        new Intl.Locale(productSheetLocale)?.language
      );
    },
  );
  if (potentialLocale) {
    return potentialLocale;
  }

  // Default language (once among all locales available for the country)
  return configByCountry?.default;
};

const localeMapping: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB',
  es: 'es-ES',
  'ca-ES': 'es-ES',
  fr: 'fr-FR',
  hr: 'hr-HR',
  hu: 'hu-HU',
  it: 'it-IT',
  lt: 'lt-LT',
  lv: 'lv-LV',
  pl: 'pl-PL',
  pt: 'pt-PT',
  nl: 'nl-NL',
  ro: 'ro-RO',
  sk: 'sk-SK',
  sl: 'sl-SI',
  sr: 'sr-RS',
  tr: 'tr-TR',
};

export const getProductSheetLocale = (locale: string) => {
  return localeMapping[locale] || locale;
};

export const getTranslationFilename = (locale: string) => {
  return localeMapping[locale] || locale;
};

/**
 *
 * @param locale
 *
 * Get language available in our application from a random locale
 */
export const retrieveAppLanguage = (
  locale: string,
  availableLanguages: string[],
): string => {
  // "locale" is defined as an available language in the app, so return it
  if (availableLanguages?.includes(locale)) {
    return locale;
  }

  // Try to find, a corresponding language in our available languages
  const localeGrabbed = locale?.split('-')?.[0];
  const potentialLocale = availableLanguages?.find(
    (availableLanguage: string) => localeGrabbed === availableLanguage,
  );
  if (potentialLocale) {
    return potentialLocale;
  }

  // Return default locale
  return 'en';
};

export const isValidLocale = (locale: string): boolean => {
  try {
    Intl.getCanonicalLocales(locale);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const isValidRegion = (region: string): boolean => {
  if (typeof region !== 'string' || !region) {
    return false;
  }

  try {
    new Intl.Locale('en', { region });
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const getComputedMessages = async (locale?: string) => {
  if (locale) {
    // Special Case 1 : fr-FR uses fr.json
    if (getTranslationFilename(locale) === 'fr-FR') {
      try {
        return (await import(`../../../../../messages/fr.json`)).default;
      } catch (e) {
        console.warn(e, 'No French messages found. (fr.json)');
      }
    }

    // Special Case 2 : en-GB uses en.json
    if (getTranslationFilename(locale) === 'en-GB') {
      try {
        return (await import(`../../../../../messages/en.json`)).default;
      } catch (e) {
        console.warn(e, 'No English messages found. (en.json)');
      }
    }

    // Regular Case
    try {
      return (
        await import(
          `../../../../../messages/${getTranslationFilename(locale)}.json`
        )
      ).default;
    } catch (e) {
      console.warn(e, `No ${locale} messages found. (${locale}.json)`);
    }
  }

  // Fallback Case (ie. locale does not have a corresponding file)
  try {
    return (await import(`../../../../../messages/en.json`)).default;
  } catch (e) {
    console.warn(e, 'No default messages found. (en.json)');
  }

  // If all else fails, return an empty object
  return {};
};

export const getTextDirection = (locale: string) => {
  if (!isValidLocale(locale)) {
    return undefined;
  }
  const intlLocale = new Intl.Locale(locale);

  /** @ts-expect-error - textInfo property is not on the Locale type yet */
  if ('textInfo' in intlLocale && intlLocale.textInfo?.direction) {
    /** @ts-expect-error */
    return intlLocale.textInfo.direction;
  }

  const rtlLanguages = ['ar', 'fa', 'he', 'iw', 'ur', 'yi', 'ji'];
  const language = intlLocale.language;

  return rtlLanguages.includes(language) ? 'rtl' : 'ltr';
};
